import React, { useCallback, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { BarChart, Label, ReferenceLine, CartesianGrid, XAxis, YAxis, Bar, ResponsiveContainer } from 'recharts';
import classnames from 'classnames';

import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';
import { InfoIcon } from 'ui-components/components/info-icon';
import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { useFillTimeSla } from '../../../../../hooks/use-user-setting';
import { emptyArray } from 'ui-components/default-props/empty';
import { bucketData, formatBucketedData, normalizeSla } from './helper';
import CustomBar from './custom-bar';
import messages from './messages';
import Colors from '../utilities/colors';

import style from './style.module.less';
import analyticsStyle from '../../../analytics-style.module.less';

const DEFAULT_WIDTH = 425;
const DEFAULT_HEIGHT = 375;
const HIDDEN_Y_AXIS_WIDTH = 100;
const WHEN_TO_SHOW_TICKS_BY_TEN = 20;
const EXTRA_ENDING_TICK = 1;
const Y_OFFSET = 0.5; // .5 offset is needed so that top and bottom bars aren't cut off
const Y_BARS_DOMAIN = [0 + Y_OFFSET, 10 + Y_OFFSET];

// num will always be divisible by 10 and 5, divide by one of those to make graph look prettier
const calculateTickCount = (num) => (num > WHEN_TO_SHOW_TICKS_BY_TEN ? num / 10 : num / 5) + EXTRA_ENDING_TICK;

const OpenOrderAgingGraph = ({ data = emptyArray, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT, onChangeSla = noop }) => {
  const intl = useIntl();
  const bucketedData = bucketData(data);
  const [formattedData, maxPercentage] = formatBucketedData(bucketedData);
  const sla = useFillTimeSla();

  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const formatXAxisTicks = useCallback(
    (tick) => {
      return intl.formatNumber(tick / 100, { style: 'percent' });
    },
    [intl],
  );

  const formatYAxisTicks = useCallback(
    (tick) => {
      return bucketedData.find((bucket) => bucket.id === tick).name;
    },
    [bucketedData],
  );

  const normalizedSla = useMemo(() => normalizeSla(sla), [sla]);

  const upperBound = Math.ceil(maxPercentage / 10) * 10 || 100;
  const tickCount = calculateTickCount(upperBound);

  const slaXCoordinates = {
    x1: -7,
    x2: upperBound + 10,
  };

  return (
    <>
      <h2
        className={classnames(
          { [style.title]: !isShipStationAnalyticsFeatureEnabled },
          {
            [analyticsStyle['analytics-table-title-h2']]: isShipStationAnalyticsFeatureEnabled,
          },
        )}
      >
        <span className={style['title-text']}>
          <FormattedMessage {...messages.title} />
        </span>
        <FormattedMessage {...messages.tooltipMessage}>
          {(formattedMessage) => <InfoIcon tooltipText={formattedMessage} />}
        </FormattedMessage>
      </h2>
      {isShipStationAnalyticsFeatureEnabled ? (
        <Text
          className={analyticsStyle['margin-16']}
          textType={TextType.Paragraph}
          value={
            <FormattedMessage
              {...messages.slaDescription}
              values={{
                sla,
                button: (chunks) => (
                  <Button onClick={onChangeSla} theme='link'>
                    {chunks}
                  </Button>
                ),
              }}
            />
          }
        />
      ) : (
        <p className={style.description}>
          <FormattedMessage
            {...messages.slaDescription}
            values={{
              sla,
              button: (chunks) => (
                <Button onClick={onChangeSla} theme='link'>
                  {chunks}
                </Button>
              ),
            }}
          />
        </p>
      )}
      {!data.length && (
        <div className={style['inline-message-container']}>
          <InlineMessage type={INLINE_MESSAGE_TYPES.info}>
            <FormattedMessage {...messages.noDataMessage} />
          </InlineMessage>
        </div>
      )}
      <ResponsiveContainer height={height} width={width}>
        <BarChart
          data={formattedData}
          layout='vertical'
          margin={{ left: -15 }}
          style={{
            fontSize: 12,
          }}
        >
          <CartesianGrid horizontal={false} stroke={Colors.GRID_COLOR} vertical />
          <XAxis
            allowDataOverflow
            dataKey='count'
            domain={[0, upperBound]}
            height={40}
            tickCount={tickCount}
            tickFormatter={formatXAxisTicks}
            tickLine={false}
            tickSize={2}
            type='number'
          >
            <Label
              className={classnames(style['yAxis-label'], {
                [analyticsStyle['analytics-yAxis-label']]: isShipStationAnalyticsFeatureEnabled,
              })}
              offset={5}
              position='insideBottom'
              value={intl.formatMessage(messages.xAxisTitle)}
            />
          </XAxis>
          <YAxis
            dataKey='id'
            domain={Y_BARS_DOMAIN}
            tickFormatter={formatYAxisTicks}
            tickLine={false}
            ticks={bucketedData.map((bucket) => bucket.id)}
            tickSize={10}
            type='number'
            width={80}
            yAxisId='left'
          >
            <Label
              angle={-90}
              className={classnames(style['yAxis-label'], {
                [analyticsStyle['analytics-yAxis-label']]: isShipStationAnalyticsFeatureEnabled,
              })}
              offset={26}
              position='insideLeft'
              value={intl.formatMessage(messages.yAxisTitle)}
            />
          </YAxis>
          {/* Second YAxis needed to provide width on right side, margin didn't work for me */}
          <YAxis orientation='right' tickCount={0} width={HIDDEN_Y_AXIS_WIDTH} yAxisId='right' />
          <Bar dataKey='count' shape={<CustomBar normalizedSla={normalizedSla} />} unit='%' yAxisId='left' />
          <ReferenceLine
            ifOverflow='extendDomain'
            isFront
            segment={[
              { x: slaXCoordinates.x1, y: normalizedSla },
              { x: slaXCoordinates.x2, y: normalizedSla },
            ]}
            stroke={Colors.SLA_LINE_COLOR}
            strokeWidth={3}
            yAxisId='left'
          >
            <Label
              position='insideBottomRight'
              style={{
                fontSize: 14,
              }}
              value={intl.formatMessage(messages.slaMessage, { sla })}
            />
          </ReferenceLine>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

OpenOrderAgingGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      age: PropTypes.number,
      count: PropTypes.number,
    }),
  ),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChangeSla: PropTypes.func,
};

export default OpenOrderAgingGraph;
